import DOMPurify from "dompurify";
import { cn } from "@/lib/utils";

const SAMPLE_VALUES: Record<string, string> = {
  contact_name: "Sarah Johnson",
  first_name: "Sarah",
  property_address: "1842 Maple Ridge Dr",
  city: "Austin",
  neighborhood: "Maple Ridge",
  street_name: "Maple Ridge Dr",
  company_name: "Summit Roofing Co.",
  company_address: "1200 Main St, Austin, TX",
  company_logo: "",
  rep_name: "Mike Rodriguez",
  rep_phone: "(512) 555-0198",
  rep_email: "mike@summitroofing.com",
  schedule_link: "#schedule",
  review_link: "#review",
  appointment_time: "Thursday at 10:30 AM",
  eta_minutes: "20",
  storm_date: "April 18",
  license_number: "TX-123456",
};

export function renderSequencePreview(html: string) {
  const withValues = Object.entries(SAMPLE_VALUES).reduce(
    (content, [key, value]) => content.replace(new RegExp(`{{\\s*${key}\\s*}}`, "g"), value),
    html || "",
  );

  const cleaned = withValues.replace(/{{\s*unsubscribe_link\s*}}/g, "#unsubscribe");

  return DOMPurify.sanitize(cleaned, {
    ADD_ATTR: ["target", "style"],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|#|\/)/i,
  });
}

interface SequencePreviewFrameProps {
  html: string;
  subject?: string | null;
  type?: string;
  className?: string;
}

export function SequencePreviewFrame({ html, subject, type = "email", className }: SequencePreviewFrameProps) {
  const isEmail = type === "email";

  if (!isEmail) {
    return (
      <div className={cn("rounded-xl border border-border bg-card p-4", className)}>
        <div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">{type === "sms" ? "SMS preview" : "Task preview"}</div>
        <div className="rounded-2xl bg-primary/10 p-4 text-sm leading-relaxed text-foreground whitespace-pre-wrap">{html}</div>
      </div>
    );
  }

  return (
    <div className={cn("overflow-hidden rounded-xl border border-border bg-card", className)}>
      <div className="border-b border-border bg-muted/40 px-4 py-3">
        <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Visual email preview</div>
        <div className="mt-1 truncate text-sm font-semibold text-foreground">{subject || "No subject"}</div>
      </div>
      <div className="bg-muted/30 p-4">
        <div className="mx-auto max-w-[680px] overflow-hidden rounded-lg bg-background shadow-sm">
          <div dangerouslySetInnerHTML={{ __html: renderSequencePreview(html) }} />
        </div>
      </div>
    </div>
  );
}
